/**
 * @fileoverview Hook de Data Layer para gestión de datos de plantillas de mensajes en NewChat.
 * Maneja únicamente la carga y procesamiento de datos de plantillas, sin lógica de UI o formularios.
 * Optimizado con memoización para prevenir renders innecesarios.
 *
 * @module useTemplateMessageData
 * @category Hook - Data Layer
 * @since 1.0.0
 * @version 1.1.0 - Separación de responsabilidades y optimización de performance
 */
import { useMemo, useCallback, useState } from 'react';

/**
 * Hook de Data Layer para gestión de datos de plantillas de mensajes en NewChat.
 */
const useTemplateMessageData = (selectedTemplate, dynamicFieldsData) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Procesa y estructura los nombres de campos dinámicos principales.
   */
  const dynamicFieldNames = useMemo(() => [
    ...(dynamicFieldsData?.main?.variables?.map((variable) => variable.fieldName) || [])
  ], [dynamicFieldsData]);

  /**
   * Procesa y estructura los datos del carousel para renderizado optimizado.
   */
  const carouselData = useMemo(() => {
    const isCarousel = selectedTemplate?.templateType === 'carousel';

    return {
      cards: selectedTemplate?.cards || [],
      carousels: dynamicFieldsData?.cards || [],
      isCarousel,
      totalCards: selectedTemplate?.cards?.length || 0,
      carouselFieldNames: isCarousel
        ? dynamicFieldsData?.cards?.flatMap((carousel) => [
          ...(carousel.body?.variables?.map((variable) => variable.fieldName) || []),
          ...((carousel.buttons || []).flatMap((button) => button.variables?.map((variable) => variable.fieldName) || []))
        ]) || []
        : []
    };
  }, [selectedTemplate, dynamicFieldsData]);

  /**
   * Función para refrescar o recargar datos de plantilla.
   */
  const refreshData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // Los datos se actualizarán automáticamente por las dependencias de useMemo
      // Esta función principalmente limpia errores y estados
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Retorna el estado completo de datos estructurado y optimizado.
   */
  return useMemo(() => ({
    selectedTemplate,
    dynamicFieldsData,
    dynamicFieldNames,
    carouselData,
    loading,
    error,
    refreshData
  }), [
    selectedTemplate,
    dynamicFieldsData,
    dynamicFieldNames,
    carouselData,
    loading,
    error,
    refreshData
  ]);
};

export default useTemplateMessageData;
